/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

/**
 * StickyConversionBar — Barre sticky qui apparaît après scroll au-delà
 * du hero. Donne au visiteur un raccourci constant vers la demande de
 * démo, où qu'il soit sur la page.
 *
 * S'affiche : scrollY > 800
 * Se masque : scrollY < 800 (retour en haut)
 * Peut être fermée par l'utilisateur (state local, jamais réaffichée
 * sur la même page).
 */
export const StickyConversionBar = ({ onDemoClick }) => {
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            const scrolled = window.scrollY > 800;
            setVisible(scrolled);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    if (dismissed) return null;

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: -60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -60, opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    className="fixed top-0 left-0 right-0 z-40 bg-[#0B0913]/95 backdrop-blur-xl border-b border-white/8 shadow-2xl shadow-black/40"
                    data-testid="sticky-conversion-bar"
                >
                    <div className="max-w-6xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="hidden sm:flex w-7 h-7 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] items-center justify-center flex-shrink-0">
                                <Sparkles className="w-3.5 h-3.5 text-white" />
                            </span>
                            <p className="text-xs sm:text-sm text-white truncate" style={DISPLAY}>
                                <span className="font-semibold">Démo Homly</span>
                                <span className="hidden sm:inline text-white/60"> · 20 minutes · sans engagement · sans argumentaire</span>
                                <span className="sm:hidden text-white/60"> · 20 min · sans engagement</span>
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                type="button"
                                onClick={onDemoClick}
                                data-testid="sticky-conversion-bar-cta"
                                className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold text-xs sm:text-sm hover:opacity-95 hover:scale-[1.02] transition-all whitespace-nowrap"
                            >
                                Réserver mon créneau
                                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setDismissed(true)}
                                aria-label="Fermer la barre"
                                data-testid="sticky-conversion-bar-dismiss"
                                className="text-white/40 hover:text-white/80 text-lg leading-none px-1"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
